/**
 * Dashboard — Screen 1
 * Range toggle, live ops strip, KPI row, AI performance report, appointments,
 * needs-attention, activity feed.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  fetchDashboardStats,
  fetchLiveState,
  fetchAttentionItems,
  fetchActivity,
  fetchSettings,
} from '../../lib/apiHelpers.js';
import { fmtTime, fmtRelative } from '../../lib/time.js';
import { useIsMobile } from '../../lib/hooks.js';
import { AiOrb } from '../../components/AiOrb.js';
import { Waveform } from '../../components/Waveform.js';
import { KpiCard } from '../../components/KpiCard.js';
import { AvatarInitials } from '../../components/AvatarInitials.js';
import { ComingSoon } from '../../components/ComingSoon.js';

type Range = 'today' | '7d' | '30d';

const RANGE_LABELS: Record<Range, string> = { today: 'Today', '7d': '7d', '30d': '30d' };

function fmtDuration(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function fmtPct(n: number): string {
  return `${n}%`;
}

// ── Live Ops Strip ───────────────────────────────────────────────────────────

function LiveOpsStrip({ aiName }: { aiName: string }) {
  const isMobile = useIsMobile();

  const { data: live } = useQuery({
    queryKey: ['live-state'],
    queryFn: fetchLiveState,
    refetchInterval: 15_000,
  });

  const callCount = live?.liveCalls?.length ?? 0;
  const queueCount = live?.queueCount ?? 0;
  const nextDialIn = live?.nextDialIn;
  const quietHours = live?.quietHoursEnforced ?? false;
  const liveCalls = live?.liveCalls ?? DEMO_LIVE_CALLS;

  // Mobile: column layout, max 1 mini-card
  const cardsToShow = isMobile ? 1 : 3;
  const subText = [
    callCount > 0 ? `${callCount} live` : null,
    queueCount > 0 ? `${queueCount} queued` : 'queue empty',
    nextDialIn ? `next in ${nextDialIn}` : null,
    quietHours ? 'quiet hours' : null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      className="rounded-[16px] p-4 mb-6"
      style={{
        background: '#0D1729',
        boxShadow: '0 8px 24px -8px rgba(13,23,41,0.3)',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 12 : 16,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
      }}
    >
      {/* Orb + text row */}
      <div className="flex items-center gap-3 min-w-0">
        <AiOrb size={32} pulse />
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-white mb-0.5">
            {aiName} is working your pipeline
          </p>
          <p
            className="text-[11px] text-white/60"
            style={{
              fontFamily: '"DM Mono", monospace',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: isMobile ? 260 : undefined,
            }}
          >
            {subText || 'standing by'}
          </p>
        </div>
        {/* Waveform sits in same row as text on desktop */}
        {!isMobile && <Waveform color="rgba(255,255,255,0.5)" height={18} />}
      </div>

      {/* Live call mini-cards */}
      {liveCalls.length > 0 && (
        <div
          className="flex gap-3"
          style={{ flexWrap: isMobile ? 'nowrap' : 'wrap' }}
        >
          {liveCalls.slice(0, cardsToShow).map((call, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-3 py-2 rounded-[10px] flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <AvatarInitials name={call.leadName} size={24} ring="green" badge />
              <div className="min-w-0">
                <p
                  className="text-[11px] font-semibold text-white"
                  style={{ whiteSpace: 'nowrap', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {call.leadName}
                </p>
                <p
                  className="text-[9.5px] text-white/50"
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    whiteSpace: 'nowrap',
                    maxWidth: 90,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {call.phase} · {fmtDuration(call.durationS)}
                </p>
              </div>
              <Link
                to="/inbox"
                className="text-[10px] font-medium text-white/70 hover:text-white whitespace-nowrap transition-colors"
              >
                Open thread
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* All N live link */}
      {callCount > cardsToShow && (
        <Link
          to="/live"
          className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-ink bg-white hover:bg-white/90 transition-colors whitespace-nowrap flex-shrink-0"
        >
          All {callCount} live →
        </Link>
      )}
    </div>
  );
}

const DEMO_LIVE_CALLS = [
  { leadName: 'Marcus Bell', phase: 'Qualifying', durationS: 134 },
  { leadName: 'Hank Willis', phase: 'Transfer ready', durationS: 67 },
  { leadName: 'Denise Okafor', phase: 'Opening', durationS: 44 },
];

// ── AI Performance card ──────────────────────────────────────────────────────

function AiPerformanceCard({ stats, aiName }: { stats: any; aiName: string }) {
  const Tile = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
    <div className="p-3 rounded-[10px]" style={{ background: '#FAFBFC' }}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-1">{label}</p>
      <p className="text-[17px] font-medium text-ink" style={{ fontFamily: '"DM Mono", monospace' }}>
        {value}
      </p>
      {sub && <p className="text-[10px] text-muted mt-0.5">{sub}</p>}
    </div>
  );

  const voiceDials = stats?.dials ?? 0;
  const voiceAnswered = stats?.answered ?? 0;
  const answerRate = stats?.answerRate ?? 0;
  const avgCallLen = stats?.avgCallLengthS ?? 0;
  const avgCallsToContact = stats?.avgCallsToContact ?? null;

  // Channel bars
  const channels = [
    { name: 'Voice', color: '#6D4AEA', pct: 26 },
    { name: 'iMessage', color: '#0B84FE', pct: 55 },
    { name: 'RCS', color: '#21A254', pct: 34 },
    { name: 'SMS', color: '#64748B', pct: 27 },
  ];

  return (
    <div className="bg-surface rounded-card shadow-card p-5">
      <h3
        className="text-[14px] font-semibold text-ink mb-4 flex items-center gap-1.5"
        style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
      >
        <span className="text-violet-DEFAULT">✦</span>
        {aiName} performance report
      </h3>

      <div className="grid grid-cols-2 gap-4 mb-5">
        {/* Voice column */}
        <div>
          <div className="flex items-center gap-1.5 mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-violet-DEFAULT">
              ☎ Voice
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Tile label="Dials" value={voiceDials > 0 ? String(voiceDials) : '—'} />
            <Tile label="Answered" value={voiceAnswered > 0 ? String(voiceAnswered) : '—'} sub={answerRate > 0 ? `+${answerRate}% answer rate` : undefined} />
            <Tile label="Avg call" value={avgCallLen > 0 ? fmtDuration(avgCallLen) : '—'} />
            <Tile label="Calls → contact" value={avgCallsToContact != null ? String(avgCallsToContact) : '—'} sub="no voicemails — redial" />
          </div>
        </div>

        {/* Messaging column — greyed */}
        <ComingSoon label="Messaging coming soon">
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-blue-DEFAULT">
                💬 Messaging
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Tile label="Sent" value="—" />
              <Tile label="Replies" value="—" sub="+—% rate" />
              <Tile label="First reply" value="—" sub="median" />
              <Tile label="Opt-outs" value="—" sub="STOP rate" />
            </div>
          </div>
        </ComingSoon>
      </div>

      {/* Contact rate bars */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-3">
          Contact rate by channel
        </p>
        <div className="flex flex-col gap-2">
          {channels.map((ch) => (
            <div key={ch.name} className="flex items-center gap-3">
              <span className="text-[11px] text-muted w-16 flex-shrink-0">{ch.name}</span>
              <div
                className="flex-1 rounded-full overflow-hidden"
                style={{ height: 7, background: '#F0F3F8' }}
              >
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${ch.pct}%`, background: ch.color }}
                />
              </div>
              <span
                className="text-[10px] text-muted w-8 text-right flex-shrink-0"
                style={{ fontFamily: '"DM Mono", monospace' }}
              >
                {ch.pct}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Insight */}
      <p className="text-[11.5px] text-violet-DEFAULT mt-4 flex items-start gap-1">
        <span className="flex-shrink-0">✦</span>
        iMessage outperforms every other channel by 21 pts — {aiName} routes there first when available.
      </p>
    </div>
  );
}

// ── Needs Attention card ─────────────────────────────────────────────────────

function NeedsAttentionCard({ aiName, timezone }: { aiName: string; timezone: string }) {
  const { data: items } = useQuery({
    queryKey: ['attention'],
    queryFn: fetchAttentionItems,
    refetchInterval: 30_000,
  });

  const list = items ?? DEMO_ATTENTION;

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      <div
        className="px-4 py-3 flex items-center gap-2"
        style={{ background: 'rgba(251,191,36,0.07)', borderBottom: '1px solid rgba(251,191,36,0.15)' }}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: '#B45309' }}>
          ⚡ Needs your attention
        </span>
        {list.length > 0 && (
          <span
            className="text-[10px] font-semibold text-white rounded-chip px-1.5 py-0.5"
            style={{ background: '#D97706', fontFamily: '"DM Mono", monospace' }}
          >
            {list.length}
          </span>
        )}
      </div>

      <div className="divide-y divide-hairline">
        {list.length === 0 ? (
          <div className="px-4 py-5 flex items-center gap-2 text-green-DEFAULT">
            <span className="text-base">✓</span>
            <span className="text-[12.5px] font-medium">
              All clear — nothing needs you right now.
            </span>
          </div>
        ) : (
          list.slice(0, 5).map((item) => (
            <div key={item.id} className="px-4 py-3 flex items-start gap-3">
              <p className="text-[12.5px] text-ink flex-1">{item.text}</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link
                  to={`/inbox`}
                  className="px-2.5 py-1 rounded-[8px] text-[11px] font-medium transition-colors"
                  style={{ background: 'rgba(46,102,224,0.08)', color: '#2E66E0' }}
                >
                  {item.action}
                </Link>
                <span
                  className="text-[10px] text-muted"
                  style={{ fontFamily: '"DM Mono", monospace' }}
                >
                  {item.ts ? fmtTime(item.ts, timezone) : '—'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const DEMO_ATTENTION = [
  {
    id: 'a1',
    leadId: 'ld_03',
    leadName: 'Kim Tran',
    text: 'Kim Tran asked about guaranteed-issue at 82 — outside Ava\'s script. Draft reply ready.',
    action: 'Review reply',
    ts: '11:09 AM',
  },
];

// ── Activity Feed ────────────────────────────────────────────────────────────

function ActivityFeed({ timezone }: { timezone: string }) {
  const { data: items } = useQuery({
    queryKey: ['activity'],
    queryFn: fetchActivity,
    refetchInterval: 30_000,
  });

  const list = items ?? DEMO_ACTIVITY;

  return (
    <div className="bg-surface rounded-card shadow-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-[14px] font-semibold text-ink"
          style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
        >
          Activity
        </h3>
        <Link to="/live" className="text-[11.5px] font-medium text-blue-DEFAULT hover:text-blue-hover transition-colors">
          Live view →
        </Link>
      </div>

      <div className="flex flex-col gap-2">
        {list.slice(0, 8).map((item) => (
          <div key={item.id} className="flex items-start gap-2">
            <span className="text-violet-DEFAULT text-[11px] flex-shrink-0 mt-0.5">✦</span>
            <p className="text-[12px] text-ink flex-1">{item.text}</p>
            <span
              className="text-[10px] text-muted flex-shrink-0"
              style={{ fontFamily: '"DM Mono", monospace' }}
            >
              {item.ts ? fmtRelative(item.ts, timezone) : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEMO_ACTIVITY = [
  { id: '1', text: 'Ava completed a 2:14 call with Marcus Bell — booked Tue 2:30 PM', ts: '12:33 PM', kind: 'call' },
  { id: '2', text: 'Denise Okafor transferred to Dan Reyes', ts: '11:31 AM', kind: 'transfer' },
  { id: '3', text: 'James Thornton enrolled in Fresh Lead — Speed to Contact', ts: '10:56 AM', kind: 'seq' },
  { id: '4', text: 'Ava left a voicemail for Linda Osei', ts: '10:05 AM', kind: 'call' },
  { id: '5', text: 'Kim Tran sent a follow-up message — flagged for review', ts: '11:09 AM', kind: 'flag' },
  { id: '6', text: 'Diane Foster arrived via webhook', ts: '11:31 AM', kind: 'lead' },
  { id: '7', text: 'Ava completed a 3:02 call with Ray Delgado — closed', ts: 'Jul 1', kind: 'close' },
  { id: '8', text: 'Tamara Nguyen booked appointment — Thu 10:30 AM', ts: 'yesterday', kind: 'book' },
];

// ── Today's Appointments ─────────────────────────────────────────────────────

function AppointmentsCard({ aiName }: { aiName: string }) {
  const DEMO_APPTS = [
    { id: '1', time: '2:30 PM', lead: 'Marcus Bell', kind: 'Presentation', closer: 'Dan Reyes', status: 'confirmed' },
    { id: '2', time: '4:00 PM', lead: 'Kevin Adeyemi', kind: 'Presentation', closer: 'Dan Reyes', status: 'confirmed' },
  ];

  const statusColor: Record<string, string> = {
    confirmed: '#159662',
    tentative: '#D97706',
    completed: '#64748B',
    no_show: '#EF4444',
  };

  return (
    <div className="bg-surface rounded-card shadow-card p-5">
      <h3
        className="text-[14px] font-semibold text-ink mb-3"
        style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
      >
        Today's appointments
      </h3>

      {DEMO_APPTS.length === 0 ? (
        <p className="text-[12.5px] text-muted italic">
          Nothing on the books today — {aiName} keeps booking into your next open slots.
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-hairline">
          {DEMO_APPTS.map((a) => (
            <div key={a.id} className="py-2.5 flex items-center gap-3">
              <span
                className="text-[12.5px] flex-shrink-0 w-14"
                style={{ fontFamily: '"DM Mono", monospace', color: '#64748B' }}
              >
                {a.time}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-ink truncate">{a.lead}</p>
                <p className="text-[11.5px] text-muted truncate">
                  {a.kind} · {a.closer}
                </p>
              </div>
              <span
                className="text-[10px] font-semibold px-2 py-0.5 rounded-chip flex-shrink-0"
                style={{
                  color: statusColor[a.status] ?? '#64748B',
                  background: `${statusColor[a.status] ?? '#64748B'}18`,
                }}
              >
                {a.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard Page ──────────────────────────────────────────────────────

interface DashboardPageProps {
  aiName: string;
}

export function DashboardPage({ aiName }: DashboardPageProps) {
  const [range, setRange] = useState<Range>('today');

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats', range],
    queryFn: () => fetchDashboardStats(range),
  });

  // Workspace timezone — cached with settings (shared with InboxPage)
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettings,
    staleTime: 5 * 60 * 1000,
  });
  const timezone = (settings as { timezone?: string } | undefined)?.timezone ?? 'America/Chicago';

  const newLeads = stats?.newLeads ?? 0;
  const contacts = stats?.contacts ?? 0;
  const contactRate = stats?.contactRate ?? 0;
  const touches = stats?.avgTouchesToContact ?? null;
  const bookRate = stats?.bookRate ?? 0;
  const transfers = stats?.liveTransfers ?? 0;

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-[22px] font-bold text-ink tracking-[-0.01em]"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            Dashboard
          </h1>
          <p className="text-[13.5px] text-muted mt-0.5">
            {contacts} contacts · {transfers} live transfers
          </p>
        </div>

        {/* Range toggle */}
        <div
          className="flex rounded-[10px] overflow-hidden"
          style={{ border: '1px solid #E5EAF2', background: '#FAFBFC' }}
        >
          {(Object.keys(RANGE_LABELS) as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className="px-3 py-1.5 text-[12px] font-medium transition-colors"
              style={
                range === r
                  ? { background: '#0D1729', color: 'white' }
                  : { color: '#64748B' }
              }
            >
              {RANGE_LABELS[r]}
            </button>
          ))}
        </div>
      </div>

      {/* Live ops strip */}
      <LiveOpsStrip aiName={aiName} />

      {/* KPI row */}
      <div
        className="grid gap-3 mb-6"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
      >
        <KpiCard label="New leads" value={newLeads} borderColor="#2E66E0" />
        <KpiCard label="Contacts" value={contacts} borderColor="#0D9488" />
        <KpiCard label="Contact rate" value={contactRate > 0 ? fmtPct(contactRate) : '—'} borderColor="#0D9488" />
        <KpiCard label="Touches → contact" value={touches != null ? String(touches) : '—'} borderColor="#6D4AEA" />
        <KpiCard label="Book rate" value={bookRate > 0 ? fmtPct(bookRate) : '—'} borderColor="#D97706" />
        <KpiCard label="Live transfers" value={transfers} borderColor="#6D4AEA" />
      </div>

      {/* Main content grid */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '1fr 320px' }}>
        {/* Left */}
        <div className="flex flex-col gap-5">
          <AiPerformanceCard stats={stats} aiName={aiName} />
          <ActivityFeed timezone={timezone} />
        </div>

        {/* Right */}
        <div className="flex flex-col gap-5">
          <AppointmentsCard aiName={aiName} />
          <NeedsAttentionCard aiName={aiName} timezone={timezone} />
        </div>
      </div>
    </div>
  );
}
